import { isSessionControlPaused } from '../stores/useDshSessionStore';
import { APPLICATION_RPC_MAX_BYTES, APPLICATION_REQUEST_TIMEOUT_MS, applicationRequest, type ApplicationAction } from '@borgee/plugin-sdk/application-request';

type Socket = Pick<WebSocket, 'readyState' | 'send'>;
interface Options { id?: string; signal?: AbortSignal; priority?: boolean; expiresAt?: number }
interface Attempt {
  channelId: string; agentId: string; encoded: string; expiresAt: number; priority: boolean; settled: boolean; canSend(): boolean;
  resolve(value: unknown): void; reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>; detach(): void;
}
export interface ApplicationRequests {
  supported(): boolean;
  enable(value: boolean): void;
  request<T = unknown>(channelId: string, agentId: string, action: ApplicationAction, input?: object, options?: Options): Promise<T>;
  receive(frame: Record<string, unknown>): void;
  resume(channelId: string): void;
  release(channelId: string): void;
  close(): void;
}
export class ApplicationRequestError extends Error {
  constructor(readonly code: string) { super(code); }
}
let active: ApplicationRequests | undefined;
export const requestAgentApplication = <T = unknown>(channelId: string, agentId: string, action: ApplicationAction, input: object = {}, signal?: AbortSignal, expiresAt?: number): Promise<T> =>
  active?.request<T>(channelId, agentId, action, input, { signal, expiresAt }) ?? Promise.reject(new ApplicationRequestError('application_connection_unavailable'));

/** A socket owns request capacity, correlation, and cancellation; sent work is never replayed. */
export function createApplicationRequests(socket: Socket, isSubscribed: (channelId: string) => boolean = () => true): ApplicationRequests {
  const queued = new Map<string, Attempt>(), sent = new Map<string, Attempt>();
  const released = new Set<string>();
  let closed = false, enabled: boolean | undefined;
  function settle(attempt: Attempt, error?: Error, value?: unknown) {
    if (attempt.settled) return;
    attempt.settled = true; attempt.detach();
    if (error) attempt.reject(error); else attempt.resolve(value);
  }
  function retire(id: string) {
    const attempt = sent.get(id) ?? queued.get(id);
    if (!attempt) return;
    sent.delete(id); queued.delete(id); clearTimeout(attempt.timer);
    queueMicrotask(drain);
    return attempt;
  }
  function drain() {
    if (!connection.supported()) return;
    while (sent.size < 16) {
      const ready = [...queued].filter(([, item]) => isSubscribed(item.channelId) && !released.has(item.channelId));
      const next = ready.find(([, item]) => item.priority) ?? ready[0];
      if (!next) break;
      const [id, attempt] = next;
      if (Date.now() >= attempt.expiresAt) { retire(id); settle(attempt, new ApplicationRequestError('application_request_unconfirmed')); continue; }
      if (!attempt.canSend()) { retire(id); settle(attempt, new ApplicationRequestError('session_control_paused')); continue; }
      queued.delete(id); sent.set(id, attempt);
      try { socket.send(attempt.encoded); }
      catch { retire(id); settle(attempt, new ApplicationRequestError('application_request_unconfirmed')); }
    }
  }
  const connection: ApplicationRequests = {
    supported: () => enabled === true && !closed && socket.readyState === 1,
    enable(value) {
      enabled = value;
      if (!value) for (const id of [...queued.keys(), ...sent.keys()]) { const item = retire(id); if (item) settle(item, new ApplicationRequestError('application_requests_unsupported')); }
      drain();
    },
    request<T>(channelId: string, agentId: string, action: ApplicationAction, input: object = {}, options: Options = {}): Promise<T> {
      const id = options.id ?? crypto.randomUUID();
      if (closed || enabled === false || released.has(channelId) || queued.size >= 256 || queued.has(id) || sent.has(id)) return Promise.reject(new ApplicationRequestError('application_connection_unavailable'));
      if (options.signal?.aborted) return Promise.reject(new ApplicationRequestError('application_request_aborted'));
      const expiresAt = options.expiresAt ?? Date.now() + APPLICATION_REQUEST_TIMEOUT_MS;
      if (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now()) return Promise.reject(new ApplicationRequestError('application_request_unconfirmed'));
      const encoded = JSON.stringify({ type: 'agent_request', client_request_id: id, channel_id: channelId, agent_id: agentId, ...applicationRequest(action, input, expiresAt) });
      if (new TextEncoder().encode(encoded).byteLength > APPLICATION_RPC_MAX_BYTES) return Promise.reject(new ApplicationRequestError('application_request_too_large'));
      return new Promise<T>((resolve, reject) => {
        const abort = () => {
          // An aborted caller does not release a request still occupying a server slot.
          if (queued.has(id)) retire(id);
          settle(attempt, new ApplicationRequestError('application_request_aborted'));
        };
        const attempt: Attempt = { channelId, agentId, encoded, expiresAt, canSend: () => !['stop_turn', 'interaction_response', 'session_control'].includes(action) || !isSessionControlPaused(channelId, agentId, (input as { binding_id?: string }).binding_id), priority: options.priority === true, settled: false,
          resolve: value => resolve(value as T), reject,
          timer: setTimeout(() => { const item = retire(id); if (item) settle(item, new ApplicationRequestError('application_request_unconfirmed')); }, Math.max(0, expiresAt - Date.now()) + 1000),
          detach: () => options.signal?.removeEventListener('abort', abort),
        };
        options.signal?.addEventListener('abort', abort, { once: true });
        queued.set(id, attempt); drain();
      });
    },
    receive(frame) {
      if (closed || frame.type !== 'agent_response' || typeof frame.client_request_id !== 'string') return;
      const item = retire(frame.client_request_id);
      if (!item) return;
      if (frame.channel_id !== item.channelId || frame.agent_id !== item.agentId || Object.hasOwn(frame, 'body') === Object.hasOwn(frame, 'error')) {
        settle(item, new ApplicationRequestError('invalid_application_response')); return;
      }
      if (frame.error) {
        const code = (frame.error as { code?: unknown }).code;
        settle(item, new ApplicationRequestError(typeof code === 'string' ? code : 'invalid_application_response'));
      } else settle(item, undefined, frame.body);
    },
    resume(channelId) { released.delete(channelId); drain(); },
    release(channelId) {
      released.add(channelId);
      for (const [id, item] of queued) if (item.channelId === channelId) { retire(id); settle(item, new ApplicationRequestError('application_channel_released')); }
      for (const item of sent.values()) if (item.channelId === channelId) settle(item, new ApplicationRequestError('application_channel_released'));
    },
    close() {
      if (closed) return;
      closed = true;
      for (const id of [...queued.keys(), ...sent.keys()]) { const item = retire(id); if (item) settle(item, new ApplicationRequestError('application_connection_closed')); }
      if (active === connection) active = undefined;
    },
  };
  return connection;
}

export function bindApplicationRequestSocket(socket: Socket, isSubscribed: (channelId: string) => boolean): ApplicationRequests {
  active?.close();
  active = createApplicationRequests(socket, isSubscribed);
  return active;
}
